import { useCallback } from "react";

import { FieldPath } from "react-hook-form";

import { CaseTemplateFormValues } from "src/cases/templates/formUtils/types";
import { useIsTemplateVersionPublished } from "src/cases/templates/utils";
import { useCapabilities } from "src/hooks/useCapabilities";

export type CaseTemplateFormFieldPath = FieldPath<CaseTemplateFormValues>;

type Action = "edit" | "add" | "delete" | "sort";

/**
 * Checks if the current user can perform an action on a field path.
 * @param action - The action to check.
 * @param fieldPath - The field path to check.
 * @returns True if the user can perform the action, false otherwise.
 */

export const useCheckFieldPermission = () => {
  const { caseTemplates } = useCapabilities();
  const isPublished = useIsTemplateVersionPublished();

  return useCallback(
    (action: Action, fieldPath: CaseTemplateFormFieldPath) => {
      if (action === "edit") {
        if (
          [
            "attributes.version_name",
            "attributes.case_creation_flow_details",
            "attributes.case_properties.*.display_name",
            "attributes.case_properties.*.values.*.color",
            "attributes.case_properties.*.values.*.display_name",
            "attributes.case_properties.*.values.*.sub_values.*.display_name",
            "attributes.file_categories.*.display_name",
            "attributes.file_categories.*.description",
            "attributes.case_tasks.*.display_name",
            "attributes.case_tasks.*.description",
            "attributes.case_tasks.*.form.fields.*.display_name",
            "attributes.case_tasks.*.form.fields.*.description",
            "attributes.case_actions.*.display_name",
            "attributes.case_actions.*.description",
            "attributes.case_actions.*.form.fields.*.display_name",
            "attributes.case_actions.*.form.fields.*.description",
            "attributes.case_blocks_configs.*.title",
            "attributes.case_blocks_configs.*.icon",
            "attributes.copilot_settings.copilot_enabled",
            "attributes.copilot_settings.copilot_prompt",
            "attributes.copilot_settings.operating_mode",
            "attributes.copilot_settings.is_run_in_sandbox_enabled",
            "attributes.copilot_settings.context_file_ids",
            "attributes.copilot_settings.websearch_tool_enabled",
          ].some((pattern) => matchesPattern(fieldPath, pattern))
        ) {
          // If it is non-breaking field and action, then there is no difference between
          // published and not published versions. We only check if user has edit capability.
          return caseTemplates.canEdit;
        }

        if (
          [
            "attributes.file_categories.*.key",
            "attributes.case_actions.*.form",
            "attributes.case_tasks.*.form",
          ].some((pattern) => matchesPattern(fieldPath, pattern))
        ) {
          // Edit available for Admin if version is not published
          return !isPublished && caseTemplates.canEdit;
        }

        // Edit available only if version is not published
        if (
          [
            "attributes.case_properties.*.key",
            "attributes.case_properties.*.type",
            "attributes.case_properties.*.values.*.value",
            "attributes.case_properties.*.values.*.sub_values.*.value",
            "attributes.case_tasks.*.key",
            "attributes.case_tasks.*.form",
            "attributes.case_tasks.*.form.fields",
            "attributes.case_tasks.*.form.fields.*.key",
            "attributes.case_tasks.*.form.fields.*.type",
            "attributes.case_tasks.*.form.fields.*.required",
            "attributes.case_tasks.*.form.fields.*.options",
            "attributes.case_actions.*.key",
            "attributes.case_actions.*.form.fields",
            "attributes.case_actions.*.form.fields.*.key",
            "attributes.case_actions.*.form.fields.*.type",
            "attributes.case_actions.*.form.fields.*.required",
            "attributes.case_actions.*.form.fields.*.options",
            "attributes.workflow_rules.*.workflow_tasks_keys",
            "attributes.workflow_rules.*.workflow_tasks_keys.*",
            "attributes.workflow_rules.*.actions.*.required_tasks_keys",
            "attributes.workflow_rules.*",
            "attributes.workflow_rules.*.actions.*.action_key",
            "attributes.case_blocks_configs.*.key",
            "attributes.case_blocks_configs.*.config.*",
          ].some((pattern) => matchesPattern(fieldPath, pattern))
        ) {
          return !isPublished && caseTemplates.canEdit;
        }

        // If nothing matches then we default to false
        return false;
      }

      if (action === "add") {
        // Anyone can add properties and children of properties in draft versions
        if (
          [
            "attributes.case_properties",
            "attributes.case_properties.*.values",
            "attributes.case_properties.*.values.*.sub_values",
            "attributes.file_categories",
            "attributes.case_tasks",
            "attributes.case_tasks.*.form.fields",
            "attributes.case_tasks.*.form.fields.*.options",
            "attributes.case_actions",
            "attributes.case_actions.*.form.fields",
            "attributes.case_actions.*.form.fields.*.options",
            "attributes.workflow_rules.*.workflow_tasks_keys",
            "attributes.workflow_rules.*.actions.*.required_tasks_keys",
            "attributes.workflow_rules",
            "attributes.case_blocks_configs",
          ].some((pattern) => matchesPattern(fieldPath, pattern))
        ) {
          // Admin users can add values or sub-values in draft versions
          return !isPublished && caseTemplates.canEdit;
        }

        // Default to false if nothing matches
        return false;
      }

      if (action === "delete") {
        if (
          [
            "attributes.case_properties",
            "attributes.case_properties.*.values",
            "attributes.case_properties.*.values.*.sub_values",
            "attributes.case_tasks",
            "attributes.case_tasks.*.form.fields",
            "attributes.case_tasks.*.form.fields.*.options",
            "attributes.case_actions",
            "attributes.case_actions.*.form.fields",
            "attributes.case_actions.*.form.fields.*.options",
            "attributes.workflow_rules.*.workflow_tasks_keys",
            "attributes.workflow_rules.*.actions.*.required_tasks_keys",
            "attributes.workflow_rules.*",
            "attributes.file_categories",
            "attributes.case_blocks_configs",
          ].some((pattern) => matchesPattern(fieldPath, pattern))
        ) {
          return !isPublished && caseTemplates.canEdit;
        }

        return false;
      }

      // Sorting is non-breaking action, generally available to all users with edit capability
      if (action === "sort") {
        if (
          [
            "attributes.case_properties",
            "attributes.case_properties.*.values",
            "attributes.case_properties.*.values.*.sub_values",
            "attributes.case_blocks_configs",
          ].some((pattern) => matchesPattern(fieldPath, pattern))
        ) {
          return caseTemplates.canEdit;
        }
        return false;
      }

      return false;
    },
    [caseTemplates.canEdit, isPublished],
  );
};

/**
 * Checks if a field path matches a pattern with wildcard support.
 *
 * Rules:
 * - If `*` is the last item in the pattern, it matches everything till the end of the path
 * - If `*` is between two dots (e.g., `foo.*.bar`), it matches any segment between dots eagerly
 *
 * @param fieldPath - The actual field path to check (e.g., "attributes.case_blocks_configs.my_block.column")
 * @param pattern - The pattern to match against (e.g., "attributes.case_blocks_configs.*")
 * @returns True if the fieldPath matches the pattern
 *
 * @example
 * matchesPattern("attributes.case_blocks_configs.my_block", "attributes.case_blocks_configs.*") // true
 * matchesPattern("attributes.case_blocks_configs.my_block.nested", "attributes.case_blocks_configs.*") // true
 * matchesPattern("attributes.other_field", "attributes.case_blocks_configs.*") // false
 * matchesPattern("attributes.foo.bar.baz", "attributes.*.baz") // true
 * matchesPattern("attributes.foo.bar", "attributes.*.baz") // false
 */
const matchesPattern = (fieldPath: string, pattern: string): boolean => {
  // If no wildcard, require exact match
  if (!pattern.includes("*")) {
    return fieldPath === pattern;
  }

  // Split both path and pattern into segments
  const pathSegments = fieldPath.split(".");
  const patternSegments = pattern.split(".");

  let pathIndex = 0;
  let patternIndex = 0;

  while (patternIndex < patternSegments.length) {
    const patternSegment = patternSegments[patternIndex];

    // If we encounter a wildcard
    if (patternSegment === "*") {
      // If wildcard is the last segment, it matches everything remaining
      if (patternIndex === patternSegments.length - 1) {
        return pathIndex < pathSegments.length;
      }

      // Otherwise, wildcard matches exactly one segment
      if (pathIndex >= pathSegments.length) {
        return false;
      }
      pathIndex++;
      patternIndex++;
    } else {
      // Non-wildcard segment must match exactly
      if (
        pathIndex >= pathSegments.length ||
        pathSegments[pathIndex] !== patternSegment
      ) {
        return false;
      }
      pathIndex++;
      patternIndex++;
    }
  }

  // After consuming all pattern segments, we should have consumed all path segments too
  return pathIndex === pathSegments.length;
};
